// File: src/pages/targets/CustomerTarget.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { Eye, Edit, ChevronDown, ChevronUp, Trash2 } from "react-feather";
import {
  FaArrowAltCircleLeft,
  FaArrowAltCircleRight,
  FaFilter,
  FaPlus,
} from "react-icons/fa";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import swal from "sweetalert";

// ---- shared imports ----
import SuperAdminUI from "../import/SuperAdminUI";
import TargetAssignedOne from "./TargetAssingnedOne";
import { CheckPermission } from "../import/CheckPermission";
import { _Delete, _Get, _PostSave } from "../import/ApiEndPoint/ApiCalling";
import {
  View_CustomerTarget_List,
  All_Users_List,
  PurchaseProductList_Product,
  Create_CustomerTarget_save, // POST new target docs (auto-save predicted)
  Delete_target_INlist, // <-- DELETE endpoint (send id in params)
} from "../import/ApiEndPoint/Api";
import { CreateCustomerList } from "../import/ApiEndPoint/ApiCalling";
import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

/* ---------- inline CSS once ---------- */
const injectOnce = () => {
  if (document.getElementById("customer-target-inline-css")) return;
  const style = document.createElement("style");
  style.id = "customer-target-inline-css";
  style.innerHTML = `
    .ct-toolbar .count-pill{background:#eef2ff;color:#3730a3;font-weight:700;padding:6px 10px;border-radius:999px;}
    .ct-card{border:1px solid #e5e7eb;border-radius:14px;background:#fff;}
    .ct-card-light{border:1px solid #e5e7eb;border-radius:14px;background:#f8fafc;}
    .ct-table-wrap{width:100%;overflow:auto;border:1px solid #e5e7eb;border-radius:14px;background:#fff;}
    .ct-table{width:100%;border-collapse:collapse;min-width:1160px;}
    .ct-table thead th{background:#f4f7f6;color:#0f172a;font-weight:700;text-transform:uppercase;font-size:12px;letter-spacing:.03em;border-bottom:1px solid #e5e7eb;padding:6px 8px;position:sticky;top:0;z-index:1;text-align:center;white-space:nowrap;}
    .ct-table tbody td{padding:8px;border-bottom:1px solid #f1f5f9;vertical-align:middle;text-align:center;font-size:14px;}
    .ct-actions{display:flex;gap:10px;justify-content:center;align-items:center;}
    .picker-box{border:1px solid #e5e7eb;border-radius:10px;max-height:280px;overflow:auto;background:#fff;padding:8px;}
    .pick-item{padding:8px 10px;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:6px;background:#fff;cursor:pointer;}
    .dropdown-ish{position:relative;}
    .dropdown-ish-menu{position:absolute;right:0;top:32px;z-index:10;border:1px solid rgb(8,91,245);background:#fff;border-radius:10px;overflow:hidden;min-width:140px;box-shadow:0 12px 24px rgba(0,0,0,.12);}
    .dropdown-ish-item{padding:10px 12px;cursor:pointer;font-weight:600;letter-spacing:.02em;}
    .dropdown-ish-item:hover{background:#f4f7f6;}
    .row-expand{background:#fbfcff;}
    .expand-toggle{display:inline-flex;align-items:center;gap:6px;cursor:pointer;font-weight:700;}
    .hl{background:#eef2ff;}
    .mini-actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
    .row-clickable{cursor:pointer;}
    .row-clickable:hover{background:#f8fafc;}
  `;
  document.head.appendChild(style);
};

/* ---------- helpers ---------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const num = (v) => (isFinite(+v) ? +v : 0);
const pick = (...xs) =>
  xs.find((x) => x !== undefined && x !== null && x !== "");

// default increment % when not available
const DEFAULT_INCREMENT_PCT = 10;

/** FY months Apr→Mar */
const fyMonths = () => {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() + 1 < 4 ? y - 1 : y;
  const months = [
    ["April", 4, startYear],
    ["May", 5, startYear],
    ["June", 6, startYear],
    ["July", 7, startYear],
    ["August", 8, startYear],
    ["September", 9, startYear],
    ["October", 10, startYear],
    ["November", 11, startYear],
    ["December", 12, startYear],
    ["January", 1, startYear + 1],
    ["February", 2, startYear + 1],
    ["March", 3, startYear + 1],
  ];
  return months.map(([name, m, y]) => ({
    key: `${name}-${y}`,
    label: `${name} ${y}`,
    monthName: name,
    m,
    y,
  }));
};
const MONTHS = fyMonths();

const monthKey = (dateLabel) => (dateLabel ? String(dateLabel) : "");
const partyKey = (idOrObj) =>
  typeof idOrObj === "object"
    ? String(idOrObj?._id || idOrObj?.sId || "")
    : String(idOrObj || "");

/** prefer doc-level incrementPercent; else line %; else default. */
const linePct = (line, docLevelPct) => {
  if (docLevelPct !== undefined && docLevelPct !== null) return +docLevelPct;
  const pct = line?.assignPercentage?.[0]?.percentage;
  return pct !== undefined && pct !== null && !isNaN(+pct)
    ? +pct
    : DEFAULT_INCREMENT_PCT;
};

export default function CustomerTarget() {
  const navigate = useNavigate();
  const { id } = useParams();
  useEffect(() => injectOnce(), []);

  // ---------- state ----------
  const [loading, setLoading] = useState(true);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [masterShow, setMasterShow] = useState(false);

  const [mergedRows, setMergedRows] = useState([]);
  const [prodDict, setProdDict] = useState(new Map());

  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedSalesId, setSelectedSalesId] = useState("");

  const [allUsers, setAllUsers] = useState([]);
  const [salesPersonIds, setSalesPersonIds] = useState([]);

  // columns modal
  const addBufferRef = useRef([]);
  const [colModal, setColModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // expand + view
  const [expanded, setExpanded] = useState({});
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  // key: `${partyKey}|${monthKey}` -> { id, doc }
  const [savedTargetByKey, setSavedTargetByKey] = useState({});
  const autoSavedOnce = useRef(new Set());

  // ---------- columns ----------
  const baseColumns = useMemo(() => {
    const actionCol = {
      key: "_actions_",
      header: "ACTIONS",
      render: (r) => (
        <div className="ct-actions" onClick={(e) => e.stopPropagation()}>
          {permissions?.View && (
            <Eye
              className="mr-50"
              size={20}
              color="green"
              onClick={() => {
                setViewRow(r);
                setViewOpen(true);
              }}
              title="View"
              style={{ cursor: "pointer" }}
            />
          )}
          {permissions?.Edit && r?._firstTargetId && (
            <Edit
              className="mr-50"
              size={20}
              color="blue"
              onClick={() =>
                navigate(`/app/customer-targets/edit/${r?._firstTargetId}`, {
                  state: r?._firstTargetDoc,
                })
              }
              title="Edit"
              style={{ cursor: "pointer" }}
            />
          )}
          {/* Delete first saved target for this party (quick access) */}
          {r?._firstTargetId && (
            <Trash2
              size={20}
              color="#b91c1c"
              title="Delete this target"
              style={{ cursor: "pointer" }}
              onClick={() => handleDeleteTarget(r._firstTargetId)}
            />
          )}
        </div>
      ),
      get: () => "",
    };

    const std = [
      { key: "uid", header: "UID", get: (_, i) => i + 1 },
      actionCol,
      {
        key: "partyName",
        header: "Party Name",
        get: (r) => r?.partyName || "",
      },
      {
        key: "salesPersonName",
        header: "Sales Person Name",
        get: (r) =>
          (r?.salesPersonName && String(r.salesPersonName).trim()) ||
          "Not Mapped",
      },
    ];

    const mcols = MONTHS.map(({ key, label }) => ({
      key,
      header: label,
      render: (row) => {
        const cell = row?.months?.[key] || { amount: 0, hasTarget: false };
        const k = `${row._partyKey}|${key}`;
        const isOpen = !!expanded[k];
        return (
          <div
            className={`expand-toggle ${isOpen ? "hl" : ""}`}
            onClick={(e) => {
              e.stopPropagation(); // do NOT trigger row-click navigation
              setExpanded((ex) => ({ ...ex, [k]: !ex[k] }));
            }}
            title={
              cell.hasTarget
                ? "Click to view details"
                : "Predicted (auto) — click to view"
            }
          >
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span>{num(cell.amount).toFixed(2)}</span>
          </div>
        );
      },
      get: (row) => toStr(row?.months?.[key]?.amount || 0),
    }));

    return [...std, ...mcols];
  }, [navigate, permissions, expanded]);

  const [selectedCols, setSelectedCols] = useState([]);
  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  useEffect(() => {
    const saved = localStorage.getItem("TargetList");
    let keys = [];
    try {
      const parsed = saved ? JSON.parse(saved) : null;
      if (Array.isArray(parsed) && parsed.length) {
        if (typeof parsed[0] === "object" && parsed[0]?.headerName) {
          keys = parsed
            .map((p) => {
              const found = baseColumns.find((c) => c.header === p.headerName);
              return found?.key || null;
            })
            .filter(Boolean);
        } else if (typeof parsed[0] === "string") {
          keys = parsed.filter((k) => colMap.has(k));
        }
      }
    } catch {}
    if (!keys.length) keys = baseColumns.map((c) => c.key);
    setSelectedCols(keys);
  }, [baseColumns, colMap]);

  const visibleCols = useMemo(
    () => selectedCols.map((k) => colMap.get(k)).filter(Boolean),
    [selectedCols, colMap]
  );

  // ---------- load data ----------
  const loadData = async (overrideId, overrideDb) => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const inside = CheckPermission("Target Creation") || {};
      setPermissions(inside);
      setMasterShow(user?.rolename?.roleName === "MASTER");
      const db = overrideDb || user?.database;

      // 1) targets
      const res = await _Get(View_CustomerTarget_List, db);
      const tlist = res?.data || [];

      // 2) customers
      let customerList = [];
      try {
        const cRes = await CreateCustomerList(user?._id, db);
        customerList = cRes?.Customer || [];
      } catch {}

      // 3) users (for SP names)
      let userList = [];
      try {
        const uRes = await _Get(All_Users_List, user?._id, db);
        userList = uRes?.User || uRes?.users || uRes?.adminDetails || [];
      } catch {}

      // 4) products
      let pDict = new Map();
      try {
        const pRes = await _Get(PurchaseProductList_Product, db);
        const list = Array.isArray(pRes?.Product) ? pRes.Product : [];
        pDict = new Map(
          list.map((p) => [
            String(p?._id || p?.sId || ""),
            p?.Product_Title || p?.Product_Desc || "",
          ])
        );
      } catch {}
      setProdDict(pDict);

      // Merge + find existing saved months
      const { merged, existingMap } = mergeAndPredict(
        tlist,
        customerList,
        userList
      );
      setMergedRows(merged);
      setSavedTargetByKey(existingMap);

      // Sales person filter options
      const spIds = Array.from(
        new Set(
          merged
            .map((r) => r?.salesPersonId)
            .filter((x) => x !== undefined && x !== null && x !== "")
        )
      );
      setAllUsers(userList);
      setSalesPersonIds(spIds);
    } catch (e) {
      console.error(e);
      setMergedRows([]);
      setSavedTargetByKey({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    loadData(user?._id, user?.database);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------- merge + predict; and build existing-map ----------
  const mergeAndPredict = (tlist, customersArr, usersArr) => {
    const cById = new Map(),
      cBySid = new Map();
    (customersArr || []).forEach((c) => {
      const cid = pick(c?._id, c?.id);
      const sid = c?.sId;
      if (cid) cById.set(String(cid), c);
      if (sid) cBySid.set(String(sid), c);
    });

    // users lookup for salesperson names
    const uById = new Map(),
      uBySid = new Map();
    (usersArr || []).forEach((u) => {
      const uid = pick(u?._id, u?.id);
      const sid = u?.sId;
      if (uid) uById.set(String(uid), u);
      if (sid) uBySid.set(String(sid), u);
    });

    const byParty = new Map();
    const existingMap = {}; // `${party}|${month}` -> {id, doc}

    for (const t of tlist || []) {
      const pk = partyKey(t?.partyId);
      if (!pk) continue;

      // party name
      const party =
        (typeof t?.partyId === "object" &&
          (cById.get(String(t?.partyId?._id)) ||
            cBySid.get(String(t?.partyId?.sId)))) ||
        cById.get(pk) ||
        cBySid.get(pk) ||
        null;
      const partyName =
        party?.CompanyName ||
        (typeof t?.partyId === "object" ? t?.partyId?.CompanyName : "") ||
        t?.customer?.CompanyName ||
        "";

      // salesperson lookup (prefer saved name on the doc, else map from user)
      const spId = pick(t?.salesPersonId);
      const spUser = spId
        ? uById.get(String(spId)) || uBySid.get(String(spId)) || null
        : null;
      const salesPersonId = spId || "";
      const nameFromDoc =
        (t?.salesPersonName && String(t.salesPersonName).trim()) || "";
      const nameFromUser =
        (spUser?.firstName &&
          `${spUser.firstName} ${spUser?.lastName || ""}`.trim()) ||
        spUser?.fullName ||
        spUser?.name ||
        "";
      const salesPersonName = nameFromDoc || nameFromUser || "Not Mapped";

      if (!byParty.has(pk)) {
        const monthsInit = {};
        MONTHS.forEach(
          ({ key }) =>
            (monthsInit[key] = {
              amount: 0,
              hasTarget: false,
              details: [],
              existingDoc: null,
            })
        );
        byParty.set(pk, {
          _partyKey: pk,
          partyName,
          salesPersonId,
          salesPersonName, // <-- keep the NAME, never the ID
          months: monthsInit,
          _firstTargetId: t?._id,
          _firstTargetDoc: t,
          rawTargets: [],
        });
      }

      const bucket = byParty.get(pk);
      const mk = monthKey(t?.date);
      const amount = (t?.products || []).reduce(
        (s, p) => s + num(p?.totalPrice ?? p.qtyAssign * p.price),
        0
      );

      if (mk && bucket.months[mk]) {
        bucket.months[mk].amount += amount;
        bucket.months[mk].hasTarget = true;
        bucket.months[mk].details.push(t);
        if (!bucket.months[mk].existingDoc) {
          bucket.months[mk].existingDoc = t;
          existingMap[`${pk}|${mk}`] = { id: t?._id, doc: t };
        }
      }
      bucket.rawTargets.push(t);
    }

    // predict (display only)
    for (const row of byParty.values()) {
      const productHist = new Map();
      for (const t of row.rawTargets) {
        const mk = monthKey(t?.date);
        const docPct = t?.incrementPercent; // may be undefined
        for (const p of t?.products || []) {
          const pid = String(p.productId);
          const qty = num(p.qtyAssign);
          const price = num(p.price);
          const amt = p.totalPrice != null ? num(p.totalPrice) : qty * price;
          const pct = linePct(p, docPct);
          const arr = productHist.get(pid) || [];
          arr.push({ mk, qty, price, amount: amt, pct, isReal: true, line: p });
          productHist.set(pid, arr);
        }
      }

      for (const [pid, arr] of productHist) {
        arr.sort((a, b) => (a.mk > b.mk ? 1 : -1));
        const realInFY = arr.find((x) => MONTHS.some((m) => m.key === x.mk));
        if (!realInFY) continue;

        let prevQty = realInFY.qty;
        let price = realInFY.price;
        const pct = realInFY.pct ?? DEFAULT_INCREMENT_PCT;

        let hitBase = false;
        for (const m of MONTHS) {
          const mk = m.key;
          const real = arr.find((x) => x.mk === mk);
          if (!hitBase && mk === realInFY.mk) {
            hitBase = true;
            continue;
          }
          if (!hitBase) continue;

          if (real) {
            prevQty = real.qty;
            price = real.price;
            continue;
          }

          // predicted future month
          prevQty = Math.round(prevQty * (1 + pct / 100) * 100) / 100;
          const predAmt = Math.round(prevQty * price * 100) / 100;

          const cell = row.months[mk];
          if (cell) {
            cell.amount += predAmt;
            cell.details.push({
              _auto: true,
              date: mk,
              products: [
                {
                  productId: pid,
                  qtyAssign: prevQty,
                  price,
                  totalPrice: predAmt,
                  assignPercentage: [{ month: m.monthName, percentage: pct }],
                },
              ],
            });
          }
        }
      }
    }

    return { merged: Array.from(byParty.values()), existingMap };
  };

  // ---------- infer a doc-level incrementPercent for a predicted month ----------
  const inferIncrementPercent = (row, mkey) => {
    const details = row?.months?.[mkey]?.details || [];
    for (const d of details) {
      for (const p of d.products || []) {
        const pct = Number(p?.assignPercentage?.[0]?.percentage);
        if (!isNaN(pct)) return pct;
      }
    }
    for (const t of row.rawTargets) {
      if (t?.incrementPercent != null) return Number(t.incrementPercent);
      const pct = Number(t?.products?.[0]?.assignPercentage?.[0]?.percentage);
      if (!isNaN(pct)) return pct;
    }
    return DEFAULT_INCREMENT_PCT;
  };

  // ---------- save a single month (new target doc) ----------
  const saveMonthForParty = async (row, mkey) => {
    const k = `${row._partyKey}|${mkey}`;
    if (savedTargetByKey[k]?.id) return; // already saved or set

    const details = row?.months?.[mkey]?.details || [];
    if (!details.length) return;

    // aggregate products
    const acc = new Map(); // pid -> { qtyAssign, price, totalPrice }
    details.forEach((d) => {
      (d?.products || []).forEach((p) => {
        const pid = String(p.productId);
        const qty = num(p.qtyAssign);
        const price = num(p.price);
        const total = p.totalPrice != null ? num(p.totalPrice) : qty * price;
        const prev = acc.get(pid) || { qtyAssign: 0, price, totalPrice: 0 };
        acc.set(pid, {
          qtyAssign: prev.qtyAssign + qty,
          price: price || prev.price || 0,
          totalPrice: prev.totalPrice + total,
        });
      });
    });

    const products = Array.from(acc.entries()).map(([pid, v]) => ({
      productId: pid,
      qtyAssign: Number(v.qtyAssign),
      price: Number(v.price),
      totalPrice: Number(v.totalPrice),
      assignPercentage: [],
    }));
    const grandTotal = products.reduce((s, p) => s + num(p.totalPrice), 0);

    // infer doc-level incrementPercent for this predicted month
    const incPct = inferIncrementPercent(row, mkey);

    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const payload = {
      date: mkey,
      grandTotal: Number(grandTotal.toFixed(2)),
      partyId: row._partyKey,
      products,
      created_by: user?._id,
      salesPersonId: row?.salesPersonId || "",
      salesPersonName: row?.salesPersonName || "Not Mapped", // ✅ persist human name on auto-saves too
      incrementPercent: Number(incPct),
    };

    try {
      const res = await _PostSave(Create_CustomerTarget_save, payload);
      if (res?.status && res?.data?._id) {
        setSavedTargetByKey((prev) => ({
          ...prev,
          [k]: { id: res.data._id, doc: res.data },
        }));
      }
    } catch (e) {
      // optional: toast; suppress to avoid blocking UI
    }
  };

  // ---------- AUTO-SAVE ALL predicted months after data loads ----------
  useEffect(() => {
    if (!mergedRows.length) return;

    const tasks = [];
    for (const row of mergedRows) {
      for (const m of MONTHS) {
        const k = `${row._partyKey}|${m.key}`;
        if (savedTargetByKey[k]?.id) continue;
        const cell = row?.months?.[m.key];
        if (!cell) continue;
        const isPredicted = !cell.existingDoc && (cell.details || []).length;
        if (!isPredicted) continue;
        if (autoSavedOnce.current.has(k)) continue;
        autoSavedOnce.current.add(k);
        tasks.push({ row, mkey: m.key });
      }
    }
    if (!tasks.length) return;

    (async () => {
      for (const t of tasks) {
        await saveMonthForParty(t.row, t.mkey);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedRows, savedTargetByKey]);

  // ---------- filters & search ----------
  const filtered = useMemo(() => {
    let list = mergedRows.slice();
    if (selectedSalesId) {
      list = list.filter(
        (r) => String(r?.salesPersonId || "") === String(selectedSalesId)
      );
    }
    if (selectedProduct) {
      list = list.filter((r) =>
        Object.values(r.months || {}).some((c) =>
          (c.details || []).some((d) =>
            (d.products || []).some(
              (p) => String(p.productId) === String(selectedProduct)
            )
          )
        )
      );
    }
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (r) =>
          toStr(r.partyName).toLowerCase().includes(term) ||
          toStr(r.salesPersonName).toLowerCase().includes(term)
      );
    }
    return list;
  }, [mergedRows, selectedSalesId, selectedProduct, search]);

  // ---------- downloads ----------
  const buildCSV = () => {
    const head = [
      "UID",
      "Party",
      "Sales Person",
      ...MONTHS.map((m) => m.label),
    ].join(",");
    const lines = filtered.map((r, i) => {
      const cells = MONTHS.map((m) =>
        num(r?.months?.[m.key]?.amount).toFixed(2)
      );
      return [
        i + 1,
        `"${toStr(r.partyName).replace(/"/g, '""')}"`,
        `"${toStr(r.salesPersonName).replace(/"/g, '""')}"`,
        ...cells,
      ].join(",");
    });
    return [head, ...lines].join("\r\n");
  };
  const exportPDF = async () =>
    await exportDataToPDF(buildCSV(), "CustomerTargetList");
  const exportCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "CustomerTargetList.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  const exportXLS = async () =>
    await convertDataCSVtoExcel(buildCSV(), "CustomerTargetList");
  const exportXLSX = async () =>
    await exportDataToExcel(buildCSV(), "CustomerTargetList");
  const exportXML = async () =>
    await convertDataCsvToXml(buildCSV(), "CustomerTargetList");

  // ---------- columns modal ----------
  const openCols = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setColModal(true);
  };
  const closeCols = () => setColModal(false);
  const handleAvailableCheck = (checked, col) => {
    const key = col.key;
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const moveRight = () =>
    setSelectedCols((prev) =>
      Array.from(new Set([...prev, ...addBufferRef.current]))
    );
  const moveLeft = () => {
    if (selectedIndex < 0) return;
    setSelectedCols((prev) => {
      const n = prev.slice();
      n.splice(selectedIndex, 1);
      return n;
    });
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    setSelectedCols((prev) => {
      const n = prev.slice();
      const el = n.splice(selectedIndex, 1)[0];
      n.splice(selectedIndex - 1, 0, el);
      return n;
    });
    setSelectedIndex((s) => s - 1);
  };
  const shiftDown = () => {
    setSelectedCols((prev) => {
      if (selectedIndex < 0 || selectedIndex >= prev.length - 1) return prev;
      const n = prev.slice();
      const el = n.splice(selectedIndex, 1)[0];
      n.splice(selectedIndex + 1, 0, el);
      setSelectedIndex((s) => s + 1);
      return n;
    });
  };
  const applyColumns = () => {
    localStorage.setItem("TargetList", JSON.stringify(selectedCols));
    closeCols();
  };

  // ---------- SuperAdmin switch ----------
  const handleParentSubmit = async () => {
    const a = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!a) return;
    const [uid, db] = String(a).split(" ");
    await loadData(uid, db);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // ---------- DELETE HANDLER ----------
  const handleDeleteTarget = async (targetId) => {
    if (!targetId) return;
    const confirm = await swal({
      title: "Delete this target?",
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirm) return;

    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      // Many of your delete endpoints are GET with id param.
      // _Get will append the id internally (ApiCalling style).
      const res = await _Delete(Delete_target_INlist, targetId, user?.database);
      if (res?.status) {
        await swal("Deleted", "Target deleted successfully.", "success");
        // Reload to refresh aggregates & month cells
        await loadData(user?._id, user?.database);
      } else {
        swal("Failed", res?.message || "Could not delete the target.", "error");
      }
    } catch (e) {
      swal("Error", "Something went wrong while deleting.", "error");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  // expanded row (no manual Save button; it auto-saves)
  const renderExpandedRow = (row, mkey) => {
    const k = `${row._partyKey}|${mkey}`;
    if (!expanded[k]) return null;
    const details = row?.months?.[mkey]?.details || [];

    // aggregate products
    const acc = new Map(); // pid => {name, targetQty, targetAmt}
    for (const d of details) {
      for (const p of d?.products || []) {
        const pid = String(p.productId);
        const name =
          prodDict.get(String(p?.productDetails?._id || p?.productId || "")) ||
          p?.productDetails?.Product_Title ||
          pid;
        const prev = acc.get(pid) || {
          name,
          targetQty: 0,
          targetAmt: 0,
          achievedQty: 0,
          achievedAmt: 0,
        };
        const tq = num(p.qtyAssign);
        const ta = num(p.totalPrice ?? p.qtyAssign * p.price);
        acc.set(pid, {
          ...prev,
          targetQty: prev.targetQty + tq,
          targetAmt: prev.targetAmt + ta,
        });
      }
    }

    const rows = Array.from(acc.values()).map((x) => {
      const shortQty = Math.max(x.targetQty - x.achievedQty, 0);
      const shortAmt = Math.max(x.targetAmt - x.achievedAmt, 0);
      return { ...x, shortQty, shortAmt, lastShortQty: 0, lastShortAmt: 0 };
    });

    const saved = savedTargetByKey[k];

    return (
      <tr className="row-expand" onClick={(e) => e.stopPropagation()}>
        <td
          colSpan={visibleCols.length}
          style={{ textAlign: "left", padding: "10px 12px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 6,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 700 }}>
              {row.partyName} — {mkey}
            </div>
            <div className="mini-actions">
              {saved ? (
                <>
                  <Badge color="success">Saved</Badge>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() =>
                      navigate(`/app/customer-targets/edit/${saved.id}`, {
                        state: saved.doc,
                      })
                    }
                    style={{ marginRight: 6 }}
                  >
                    <Edit size={14} style={{ marginRight: 6 }} /> Edit
                  </Button>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteTarget(saved.id)}
                    title="Delete this month target"
                  >
                    <Trash2 size={14} style={{ marginRight: 6 }} /> Delete
                  </Button>
                </>
              ) : (
                <Badge color="secondary">Saving…</Badge>
              )}
            </div>
          </div>

          <div className="ct-table-wrap" style={{ borderRadius: 10 }}>
            <table className="ct-table" style={{ minWidth: 960 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Product</th>
                  <th>Target Qty</th>
                  <th>Target Amount</th>
                  <th>Achieved Qty</th>
                  <th>Achieved Amount</th>
                  <th>Shortfall Qty</th>
                  <th>Shortfall Amount</th>
                  <th>Last Month Shortfall Qty</th>
                  <th>Last Month Shortfall Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 12, color: "#6b7280" }}>
                      No line items…
                    </td>
                  </tr>
                ) : (
                  rows.map((x, i) => (
                    <tr key={i}>
                      <td style={{ textAlign: "left" }}>{x.name}</td>
                      <td>{num(x.targetQty).toFixed(2)}</td>
                      <td>{num(x.targetAmt).toFixed(2)}</td>
                      <td>
                        <Badge
                          color={x.achievedQty > 0 ? "success" : "secondary"}
                        >
                          {num(x.achievedQty).toFixed(2)}
                        </Badge>
                      </td>
                      <td>{num(x.achievedAmt).toFixed(2)}</td>
                      <td>{num(x.shortQty).toFixed(2)}</td>
                      <td>{num(x.shortAmt).toFixed(2)}</td>
                      <td>{num(x.lastShortQty).toFixed(2)}</td>
                      <td>{num(x.lastShortAmt).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="ct-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="ct-card-light" style={{ padding: 10 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Customer Target List{" "}
                <span className="count-pill">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 120px" }} />

              {masterShow && (
                <SuperAdminUI
                  onDropdownChange={handleDropdownChange}
                  onSubmit={handleParentSubmit}
                />
              )}

              <Input
                className="input-color"
                placeholder="search party / salesperson..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ background: "#fff", width: 240, height: 32 }}
              />

              {(() => {
                const userData = JSON.parse(
                  localStorage.getItem("userData") || "{}"
                );
                const isNotCustomer =
                  userData?.rolename?.roleName !== "Customer";
                const isNotSalesPerson =
                  userData?.rolename?.roleName !== "Sales Person";
                return (
                  isNotCustomer &&
                  isNotSalesPerson &&
                  permissions?.Create && (
                    <Button
                      style={{
                        cursor: "pointer",
                        backgroundColor: "rgb(8, 91, 245)",
                        color: "white",
                        fontWeight: 600,
                        height: 32,
                      }}
                      onClick={() => navigate("/app/customer-targets/create/0")}
                    >
                      <FaPlus size={12} /> Create Target
                    </Button>
                  )
                );
              })()}

              {/* SuperAdmin Sales Person filter (name mapped by id) */}
              {(() => {
                const userData = JSON.parse(
                  localStorage.getItem("userData") || "{}"
                );
                if (userData?.rolename?.roleName !== "SuperAdmin") return null;

                const nameById = new Map();
                allUsers
                  .filter((u) => u?.rolename?.roleName === "Sales Person")
                  .forEach((u) => {
                    const id = String(u?._id || u?.sId || "");
                    const nm =
                      (u.firstName &&
                        `${u.firstName} ${u?.lastName || ""}`.trim()) ||
                      u?.name ||
                      id;
                    if (id) nameById.set(id, nm);
                  });

                const options = Array.from(new Set(salesPersonIds)).map(
                  (id) => ({
                    id,
                    name: nameById.get(String(id)) || id,
                  })
                );
                return (
                  <Input
                    type="select"
                    value={selectedSalesId}
                    onChange={(e) => setSelectedSalesId(e.target.value)}
                    style={{
                      height: 32,
                      width: 220,
                      fontWeight: 500,
                      borderRadius: 6,
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <option value="">All Sales Persons</option>
                    {options.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </Input>
                );
              })()}

              {/* Product filter */}
              <Input
                type="select"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                style={{
                  height: 32,
                  width: 240,
                  fontWeight: 500,
                  borderRadius: 6,
                  backgroundColor: "#f8f9fa",
                }}
              >
                <option value="">Filter by Product</option>
                {Array.from(prodDict.entries()).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Input>

              <Button
                outline
                color="primary"
                onClick={openCols}
                title="Filter Columns"
                style={{ height: 32 }}
              >
                <FaFilter size={16} />
              </Button>

              <div
                className="dropdown-ish"
                onMouseEnter={() => setIsDownloadOpen(true)}
                onMouseLeave={() => setIsDownloadOpen(false)}
              >
                <ImDownload
                  style={{ cursor: "pointer" }}
                  title="download file"
                  size={24}
                  color="rgb(8, 91, 245)"
                />
                {isDownloadOpen && (
                  <div className="dropdown-ish-menu">
                    <div className="dropdown-ish-item" onClick={exportPDF}>
                      .PDF
                    </div>
                    <div className="dropdown-ish-item" onClick={exportCSV}>
                      .CSV
                    </div>
                    <div className="dropdown-ish-item" onClick={exportXLS}>
                      .XLS
                    </div>
                    <div className="dropdown-ish-item" onClick={exportXLSX}>
                      .XLSX
                    </div>
                    <div className="dropdown-ish-item" onClick={exportXML}>
                      .XML
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card className="ct-card" style={{ padding: 8 }}>
            <div className="ct-table-wrap">
              <table className="ct-table">
                <thead>
                  <tr>
                    {visibleCols.map((c) => (
                      <th key={c.key}>{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleCols.length}
                        style={{
                          padding: 24,
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row, i) => (
                      <React.Fragment key={row?._partyKey || i}>
                        <tr
                          className="row-clickable"
                          onClick={() => {
                            if (row?._firstTargetId) {
                              navigate(
                                `/app/customer-targets/edit/${row._firstTargetId}`,
                                {
                                  state: row?._firstTargetDoc,
                                }
                              );
                            } else {
                              // no saved doc yet → go to create with this party preselected
                              navigate(
                                `/app/customer-targets/create/${row._partyKey}`
                              );
                            }
                          }}
                        >
                          {visibleCols.map((c) => (
                            <td key={c.key}>
                              {typeof c.render === "function"
                                ? c.render(row, i)
                                : toStr(c.get(row, i))}
                            </td>
                          ))}
                        </tr>
                        {MONTHS.map((m) => renderExpandedRow(row, m.key))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Columns Modal */}
      <Modal
        isOpen={colModal}
        toggle={closeCols}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeCols}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div className="picker-box">
                {baseColumns
                  .filter((c) => c.key !== "_actions_")
                  .map((c) => (
                    <div key={c.key} className="pick-item">
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            handleAvailableCheck(e.target.checked, c)
                          }
                        />
                        <span>{c.header}</span>
                      </label>
                    </div>
                  ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={moveRight}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col
              lg="2"
              md="2"
              sm="12"
              className="d-flex align-items-center justify-content-center"
            >
              <div className="d-grid" style={{ gap: 12 }}>
                <FaArrowAltCircleRight
                  onClick={moveRight}
                  className="arrowassign"
                  size="30px"
                  style={{ cursor: "pointer" }}
                />
                <FaArrowAltCircleLeft
                  onClick={moveLeft}
                  className="arrowassign"
                  size="30px"
                  style={{ cursor: "pointer" }}
                />
              </div>
            </Col>

            <Col lg="6" md="6" sm="12">
              <Row>
                <Col lg="8" md="8" sm="12">
                  <h5>Visible Columns</h5>
                  <div className="picker-box">
                    {selectedCols.map((k, i) => {
                      const col = colMap.get(k);
                      if (!col) return null;
                      const active = i === selectedIndex;
                      return (
                        <div
                          key={k}
                          className="pick-item"
                          onClick={() => setSelectedIndex(i)}
                          style={{
                            background: active ? "#1877f2" : "#fff",
                            color: active ? "#fff" : "#000",
                          }}
                        >
                          <IoMdRemoveCircleOutline
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCols((prev) => {
                                const next = prev.filter((x) => x !== k);
                                setSelectedIndex((si) =>
                                  si >= next.length ? -1 : si
                                );
                                return next;
                              });
                            }}
                            size={22}
                            color="red"
                            style={{ marginRight: 8, cursor: "pointer" }}
                          />
                          {col.header}
                        </div>
                      );
                    })}
                  </div>
                </Col>
                <Col
                  lg="4"
                  md="4"
                  sm="12"
                  className="d-flex align-items-center justify-content-center"
                >
                  <div className="d-grid" style={{ gap: 12 }}>
                    <BsFillArrowUpSquareFill
                      size={30}
                      onClick={shiftUp}
                      style={{ cursor: "pointer" }}
                    />
                    <BsFillArrowDownSquareFill
                      size={30}
                      onClick={shiftDown}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                </Col>
              </Row>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* View modal */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        className="modal-dialog modal-xl"
        size="lg"
        backdrop
        fullscreen
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          View Details
        </ModalHeader>
        <ModalBody className="myproducttable">
          <TargetAssignedOne ViewData={viewRow} />
        </ModalBody>
      </Modal>
    </>
  );
}
